import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../api/axios';
import { formatIndianDate, formatIndianDateTime } from '../utils/format';
import ConfirmModal from './ConfirmModal';
import { useToast } from './ToastHost';

const Calendar = ({ user }) => {
  const toast = useToast();
  const [events, setEvents] = useState([]);
  const [eventsPage, setEventsPage] = useState(1);
  const [eventsLimit] = useState(20);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionBusy, setActionBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const confirmActionRef = useRef(null);
  const [view, setView] = useState('MONTH');
  const [activeMonth, setActiveMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const [selectedDay, setSelectedDay] = useState(null);
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [eventModalMode, setEventModalMode] = useState('create');
  const [activeEvent, setActiveEvent] = useState(null);

  const [formData, setFormData] = useState({
    owner_id: '',
    project_id: '',
    title: '',
    description: '',
    start_at: '',
    remind_at: '',
    channel: 'IN_APP'
  });

  const canAssignOwner = useMemo(() => user.role === 'ADMIN' || user.role === 'BDM', [user.role]);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const requests = [api.get('/api/events', { params: { all: true } }), api.get('/api/projects')];
      if (user.role === 'ADMIN' || user.role === 'BDM') {
        requests.push(api.get('/api/users'));
      }

      const [eventsRes, projectsRes, usersRes] = await Promise.all(requests);
      setEvents(eventsRes.data || []);
      setEventsTotal(Array.isArray(eventsRes.data) ? eventsRes.data.length : 0);
      setProjects(projectsRes.data || []);
      if (usersRes) setUsers(usersRes.data || []);

      setError('');
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load calendar');
    } finally {
      setLoading(false);
    }
  };

  const fetchEventsPage = async (nextPage = eventsPage) => {
    try {
      const res = await api.get('/api/events', { params: { page: nextPage, limit: eventsLimit } });
      const payload = res.data;
      if (payload && typeof payload === 'object' && Array.isArray(payload.items)) {
        setEvents(payload.items);
        setEventsTotal(payload.total || 0);
        setEventsPage(payload.page || nextPage);
      } else {
        setEvents(Array.isArray(payload) ? payload : []);
        setEventsTotal(Array.isArray(payload) ? payload.length : 0);
        setEventsPage(nextPage);
      }
      setError('');
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load events');
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  useEffect(() => {
    if (view === 'LIST') {
      fetchEventsPage(1);
    } else {
      fetchAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setActionBusy(true);
      const payload = {
        ...formData,
        owner_id: canAssignOwner ? (formData.owner_id || user.id) : user.id,
        project_id: formData.project_id || null,
        start_at: formData.start_at || null,
        remind_at: formData.remind_at || null
      };

      if (eventModalMode === 'edit' && activeEvent?.id) {
        await api.put(`/api/events/${activeEvent.id}`, payload);
        toast.success('Event updated');
      } else {
        await api.post('/api/events', payload);
        toast.success('Event created');
      }

      setFormData({
        owner_id: '',
        project_id: '',
        title: '',
        description: '',
        start_at: '',
        remind_at: '',
        channel: 'IN_APP'
      });
      if (view === 'LIST') {
        fetchEventsPage(1);
      } else {
        fetchAll();
      }
      setEventModalOpen(false);
      setActiveEvent(null);
      setEventModalMode('create');
    } catch (e2) {
      setError(e2.response?.data?.error || 'Failed to create event');
      toast.error(eventModalMode === 'edit' ? 'Failed to update event' : 'Failed to create event');
    } finally {
      setActionBusy(false);
    }
  };

  const deleteEvent = async (id) => {
    confirmActionRef.current = async () => {
      try {
        setActionBusy(true);
        await api.delete(`/api/events/${id}`);
        if (view === 'LIST') {
          fetchEventsPage(1);
        } else {
          fetchAll();
        }
        toast.success('Event deleted');
      } catch (e) {
        setError(e.response?.data?.error || 'Failed to delete event');
        toast.error('Failed to delete event');
      } finally {
        setActionBusy(false);
        setEventModalOpen(false);
        setActiveEvent(null);
        setEventModalMode('create');
      }
    };
    setConfirmOpen(true);
  };

  const ownerOptions = useMemo(() => {
    if (user.role === 'ADMIN') {
      return users;
    }
    if (user.role === 'BDM') {
      return [{ id: user.id, name: `${user.name} (Me)` }, ...users];
    }
    return [{ id: user.id, name: `${user.name} (Me)` }];
  }, [user, users]);

  const upcoming = useMemo(() => {
    const now = new Date();
    return [...events]
      .filter((ev) => {
        const ts = ev.remind_at || ev.start_at || ev.created_at;
        if (!ts) return true;
        return new Date(ts) >= new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      })
      .sort((a, b) => new Date(a.remind_at || a.start_at || a.created_at) - new Date(b.remind_at || b.start_at || b.created_at));
  }, [events]);

  const monthTitle = useMemo(() => {
    return new Intl.DateTimeFormat('en-IN', { month: 'long', year: 'numeric' }).format(activeMonth);
  }, [activeMonth]);

  const monthOptions = useMemo(() => {
    const year = activeMonth.getFullYear();
    return Array.from({ length: 12 }, (_, i) => ({
      value: i,
      label: new Intl.DateTimeFormat('en-IN', { month: 'long' }).format(new Date(year, i, 1))
    }));
  }, [activeMonth]);

  const dayKey = (d) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const eventDateKey = (ev) => {
    const ts = ev.remind_at || ev.start_at || ev.created_at;
    if (!ts) return null;
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return null;
    return dayKey(new Date(d.getFullYear(), d.getMonth(), d.getDate()));
  };

  const eventsByDay = useMemo(() => {
    const map = new Map();
    for (const ev of events) {
      const k = eventDateKey(ev);
      if (!k) continue;
      const list = map.get(k) || [];
      list.push(ev);
      map.set(k, list);
    }
    for (const [k, list] of map.entries()) {
      list.sort((a, b) => new Date(a.remind_at || a.start_at || a.created_at) - new Date(b.remind_at || b.start_at || b.created_at));
      map.set(k, list);
    }
    return map;
  }, [events]);

  const monthGrid = useMemo(() => {
    const start = new Date(activeMonth.getFullYear(), activeMonth.getMonth(), 1);
    const end = new Date(activeMonth.getFullYear(), activeMonth.getMonth() + 1, 0);

    const startDay = start.getDay();
    const gridStart = new Date(start);
    gridStart.setDate(start.getDate() - startDay);

    const endDay = end.getDay();
    const gridEnd = new Date(end);
    gridEnd.setDate(end.getDate() + (6 - endDay));

    const days = [];
    const cursor = new Date(gridStart);
    while (cursor <= gridEnd) {
      days.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    return days;
  }, [activeMonth]);

  const goPrevMonth = () => {
    setActiveMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1));
  };

  const goNextMonth = () => {
    setActiveMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1));
  };

  const goToday = () => {
    const now = new Date();
    setActiveMonth(new Date(now.getFullYear(), now.getMonth(), 1));
  };

  const toLocalDateTimeValue = (d, hour, minute) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = String(hour).padStart(2, '0');
    const mi = String(minute).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
  };

  const openCreateForDay = (d) => {
    const remindDefault = toLocalDateTimeValue(d, 9, 0);
    const startDefault = toLocalDateTimeValue(d, 10, 0);

    setSelectedDay(d);
    setActiveEvent(null);
    setEventModalMode('create');
    setFormData((prev) => ({
      ...prev,
      start_at: startDefault,
      remind_at: remindDefault
    }));
    setEventModalOpen(true);
  };

  const openCreateForToday = () => {
    const now = new Date();
    openCreateForDay(new Date(now.getFullYear(), now.getMonth(), now.getDate()));
  };

  const openCreateForSelectedOrToday = () => {
    if (selectedDay) {
      openCreateForDay(new Date(selectedDay.getFullYear(), selectedDay.getMonth(), selectedDay.getDate()));
      return;
    }
    openCreateForToday();
  };

  const openEventView = (ev) => {
    setActiveEvent(ev);
    setEventModalMode('view');
    setEventModalOpen(true);
  };

  const startEditEvent = () => {
    if (!activeEvent) return;
    setEventModalMode('edit');
    setFormData({
      owner_id: activeEvent.owner_id ? String(activeEvent.owner_id) : '',
      project_id: activeEvent.project_id ? String(activeEvent.project_id) : '',
      title: activeEvent.title || '',
      description: activeEvent.description || '',
      start_at: activeEvent.start_at ? String(activeEvent.start_at).slice(0, 16) : '',
      remind_at: activeEvent.remind_at ? String(activeEvent.remind_at).slice(0, 16) : '',
      channel: activeEvent.channel || 'IN_APP'
    });
  };

  if (loading) {
    return <div className="text-center p-8">Loading calendar...</div>;
  }

  const totalPages = Math.max(Math.ceil(eventsTotal / eventsLimit) || 1, 1);
  const canPrev = eventsPage > 1;
  const canNext = eventsPage < totalPages;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Calendar & Reminders</h1>
      </div>

      {error && (
        <div className="error-message mb-6">
          <span className="error-text">{error}</span>
        </div>
      )}

      <div className="glass-card p-6">
        <div className="calendar-toolbar">
          <div className="calendar-toolbar-left">
            <h2 className="text-xl font-semibold">{monthTitle}</h2>
            <div className="text-sm text-tertiary">Showing events you can access</div>
          </div>
          <div className="calendar-toolbar-right">
            {view === 'MONTH' ? (
              <select
                className="glass-select"
                value={activeMonth.getMonth()}
                onChange={(e) => setActiveMonth(new Date(activeMonth.getFullYear(), Number(e.target.value), 1))}
              >
                {monthOptions.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            ) : null}
            <button type="button" className="glass-button" onClick={goToday}>Today</button>
            <button type="button" className="glass-button" onClick={goPrevMonth} aria-label="Previous month">←</button>
            <button type="button" className="glass-button" onClick={goNextMonth} aria-label="Next month">→</button>
            <div className="calendar-view-toggle">
              <button
                type="button"
                className={`glass-button ${view === 'MONTH' ? 'primary' : ''}`}
                onClick={() => setView('MONTH')}
              >
                Month
              </button>
              <button
                type="button"
                className={`glass-button ${view === 'LIST' ? 'primary' : ''}`}
                onClick={() => setView('LIST')}
              >
                List
              </button>
            </div>
          </div>
        </div>

        {view === 'MONTH' ? (
          <div>
            <div className="calendar-weekdays">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                <div key={d} className="calendar-weekday">{d}</div>
              ))}
            </div>
            <div className="calendar-grid">
              {monthGrid.map((d) => {
                const inMonth = d.getMonth() === activeMonth.getMonth();
                const k = dayKey(d);
                const list = eventsByDay.get(k) || [];
                const maxShow = 3;
                const visible = list.slice(0, maxShow);
                const extra = Math.max(0, list.length - maxShow);
                const today = new Date();
                const isToday = d.getFullYear() === today.getFullYear() && d.getMonth() === today.getMonth() && d.getDate() === today.getDate();
                const isSelected = selectedDay && d.getFullYear() === selectedDay.getFullYear() && d.getMonth() === selectedDay.getMonth() && d.getDate() === selectedDay.getDate();

                return (
                  <button
                    key={k}
                    type="button"
                    className={`calendar-day ${inMonth ? '' : 'muted'}${isToday ? ' today' : ''}${isSelected ? ' selected' : ''}`}
                    onClick={() => {
                      setSelectedDay(d);
                    }}
                    title="Select day"
                  >
                    <div className="calendar-day-header">
                      <div className="calendar-day-number">{d.getDate()}</div>
                      <div className="calendar-day-date">{formatIndianDate(d)}</div>
                    </div>
                    <div className="calendar-day-events">
                      {visible.map((ev) => (
                        <div
                          key={ev.id}
                          className={`calendar-event-chip channel-${String(ev.channel || '').toLowerCase()}`}
                          title={`${ev.title}${ev.remind_at || ev.start_at ? `\n${formatIndianDateTime(ev.remind_at || ev.start_at)}` : ''}${ev.created_by_name ? `\nOwner: ${ev.created_by_name}` : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            openEventView(ev);
                          }}
                        >
                          <div className="calendar-event-title">{ev.title}</div>
                          <div className="calendar-event-meta">
                            {ev.remind_at ? formatIndianDateTime(ev.remind_at) : ''}
                          </div>
                        </div>
                      ))}
                      {extra > 0 && <div className="calendar-event-more">+{extra} more</div>}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {totalPages > 1 ? (
              <div className="flex items-center justify-between">
                <div className="text-sm text-tertiary">Page {eventsPage} of {totalPages} ({eventsTotal} events)</div>
                <div className="flex gap-2">
                  <button type="button" className="glass-button" onClick={() => fetchEventsPage(eventsPage - 1)} disabled={!canPrev}>
                    Prev
                  </button>
                  <button type="button" className="glass-button" onClick={() => fetchEventsPage(eventsPage + 1)} disabled={!canNext}>
                    Next
                  </button>
                </div>
              </div>
            ) : null}

            {upcoming.length === 0 ? (
              <div className="text-secondary">No events found</div>
            ) : (
              upcoming.map((ev) => (
                <div key={ev.id} className="p-4 rounded-lg" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="font-semibold">{ev.title}</div>
                      <div className="text-xs text-tertiary mt-1">
                        {ev.project_title ? `Project: ${ev.project_title} • ` : ''}
                        {formatIndianDateTime(ev.remind_at || ev.start_at || ev.created_at)}
                        {ev.created_by_name ? ` • Created by: ${ev.created_by_name}` : ''}
                      </div>
                      {ev.description ? <div className="text-sm text-secondary mt-2">{ev.description}</div> : null}
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span className="status-badge">{ev.channel}</span>
                      <button type="button" className="glass-button danger text-sm px-3 py-1" onClick={() => deleteEvent(ev.id)} disabled={actionBusy || confirmBusy}>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        <ConfirmModal
          open={confirmOpen}
          title="Delete event"
          message="Delete this event?"
          confirmText="Delete"
          cancelText="Cancel"
          busy={confirmBusy}
          onCancel={() => {
            if (confirmBusy) return;
            setConfirmOpen(false);
          }}
          onConfirm={async () => {
            const action = confirmActionRef.current;
            if (!action) {
              setConfirmOpen(false);
              return;
            }
            try {
              setConfirmBusy(true);
              await action();
              setConfirmOpen(false);
            } finally {
              setConfirmBusy(false);
            }
          }}
        />
      </div>

      <button type="button" className="calendar-fab" onClick={openCreateForSelectedOrToday} aria-label="Add event">
        +
      </button>

      {eventModalOpen ? (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal" style={{ width: 'min(700px, 100%)' }}>
            <div className="modal-header">
              <h3 className="text-lg font-semibold">
                {eventModalMode === 'edit' ? 'Edit Event' : eventModalMode === 'view' ? 'Event Details' : 'Add Event'}
              </h3>
              <button type="button" className="glass-button" onClick={() => { setEventModalOpen(false); setActiveEvent(null); setEventModalMode('create'); }} disabled={actionBusy}>
                ✕
              </button>
            </div>

            <div className="modal-body">
              {eventModalMode === 'view' && activeEvent ? (
                <div className="grid gap-3">
                  <div>
                    <div className="text-sm text-tertiary">Title</div>
                    <div className="font-semibold">{activeEvent.title}</div>
                  </div>
                  {activeEvent.description ? (
                    <div>
                      <div className="text-sm text-tertiary">Description</div>
                      <div className="text-secondary">{activeEvent.description}</div>
                    </div>
                  ) : null}
                  <div className="text-sm text-tertiary">
                    {activeEvent.remind_at ? `Remind: ${formatIndianDateTime(activeEvent.remind_at)}` : ''}
                    {activeEvent.start_at ? ` • Start: ${formatIndianDateTime(activeEvent.start_at)}` : ''}
                    {activeEvent.created_by_name ? ` • Owner: ${activeEvent.created_by_name}` : ''}
                  </div>
                  <div className="status-badge">{activeEvent.channel}</div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
                  {canAssignOwner && (
                    <div>
                      <label className="block text-sm font-medium mb-2">Owner</label>
                      <select name="owner_id" value={formData.owner_id} onChange={handleChange} className="glass-select w-full">
                        <option value="">Default (Me)</option>
                        {ownerOptions.map((u) => (
                          <option key={u.id} value={u.id}>{u.name}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium mb-2">Project (optional)</label>
                    <select name="project_id" value={formData.project_id} onChange={handleChange} className="glass-select w-full">
                      <option value="">None</option>
                      {projects.map((p) => (
                        <option key={p.id} value={p.id}>{p.title}</option>
                      ))}
                    </select>
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-2">Title</label>
                    <input name="title" value={formData.title} onChange={handleChange} className="glass-input w-full" required />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-2">Description</label>
                    <textarea name="description" value={formData.description} onChange={handleChange} className="glass-textarea w-full" rows="3" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Event Date/Time (optional)</label>
                    <input type="datetime-local" name="start_at" value={formData.start_at} onChange={handleChange} className="glass-input w-full" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Remind At</label>
                    <input type="datetime-local" name="remind_at" value={formData.remind_at} onChange={handleChange} className="glass-input w-full" required />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Alert Channel</label>
                    <select name="channel" value={formData.channel} onChange={handleChange} className="glass-select w-full">
                      <option value="IN_APP">In-App</option>
                      <option value="EMAIL">Email</option>
                      <option value="BOTH">Both</option>
                    </select>
                  </div>

                  <div className="col-span-2" />

                  <div className="modal-footer col-span-2">
                    <button type="button" className="glass-button" onClick={() => { setEventModalOpen(false); setActiveEvent(null); setEventModalMode('create'); }} disabled={actionBusy}>
                      Cancel
                    </button>
                    <button type="submit" className="glass-button primary" disabled={actionBusy}>
                      {actionBusy ? 'Please wait...' : 'Save'}
                    </button>
                  </div>
                </form>
              )}
            </div>

            {eventModalMode === 'view' && activeEvent ? (
              <div className="modal-footer">
                <button type="button" className="glass-button" onClick={() => { setEventModalOpen(false); setActiveEvent(null); setEventModalMode('create'); }} disabled={actionBusy}>
                  Close
                </button>
                <button type="button" className="glass-button" onClick={startEditEvent} disabled={actionBusy}>
                  Edit
                </button>
                <button type="button" className="glass-button danger" onClick={() => deleteEvent(activeEvent.id)} disabled={actionBusy}>
                  Delete
                </button>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Calendar;
